import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { ButtonLink } from "@/components/ui-ext/Button";
import { AUTHORS, getAuthor, type Author } from "@/content/authors";
import { listBlogPosts } from "@/content/blog";
import { pageMeta, ldScripts, breadcrumbLd, faqLd, ORG_ID, SITE_ORIGIN } from "@/lib/seo";
import { siteConfig } from "@/config/site";

import { Picture } from "@/components/media/Picture";

export const Route = createFileRoute("/authors/$slug")({
  loader: ({ params }) => {
    const author = getAuthor(params.slug);
    if (!author) throw notFound();
    return { author };
  },
  head: ({ params, loaderData }) => {
    const author = loaderData?.author ?? AUTHORS[0];
    const path = `/authors/${params.slug}/`;
    const base = pageMeta({
      path,
      title: `${author.name} — SEO & AI Search Author | ${siteConfig.name}`,
      description: `${author.name}, ${author.role}. Field notes on technical SEO, local SEO, and AI search from live Texas engagements.`,
      type: "profile",
      keywords: [author.name, `${siteConfig.contact.address.city} SEO expert`, "AI search", "author"],
    });
    return {
      ...base,
      scripts: ldScripts(
        {
          "@context": "https://schema.org",
          "@type": "ProfilePage",
          "@id": `${SITE_ORIGIN}${path}#profile`,
          url: `${SITE_ORIGIN}${path}`,
          mainEntity: {
            "@type": "Person",
            "@id": `${SITE_ORIGIN}${path}#person`,
            name: author.name,
            jobTitle: author.role,
            description: author.bio[0],
            image: author.photo.startsWith("http") ? author.photo : `${SITE_ORIGIN}${author.photo}`,
            email: `mailto:${author.email}`,
            url: `${SITE_ORIGIN}${path}`,
            knowsAbout: author.expertise,
            sameAs: author.sameAs,
            worksFor: { "@id": ORG_ID },
          },
        },
        breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blog/" },
          { name: author.name, path },
        ]),
        ...(author.faqs?.length ? [faqLd(author.faqs)] : []),
      ),
    };
  },
  notFoundComponent: () => (
    <div className="container-page py-32">
      <h1 className="font-display text-4xl">Author not found</h1>
      <Link to={"/blog/" as any}     className="mt-4 inline-block text-primary">← Back to the publication</Link>
    </div>
  ),
  component: AuthorPage,
});

function AuthorPage() {
  const { author } = Route.useLoaderData() as { author: Author };
  const posts = listBlogPosts().filter((p) => p.authorSlug === author.slug);

  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <section className="container-page pt-16 pb-12">
          <EyebrowLabel>Author</EyebrowLabel>
          <div className="mt-6 grid gap-8 md:grid-cols-[200px_minmax(0,1fr)] md:items-start">
            <Picture
              variantKey={author.photoVariant}
              alt={`${author.name}, ${author.role}`}
              width={400}
              height={400}
              sizes="(min-width: 768px) 200px, 160px"
              priority
              className="block shrink-0"
              imgClassName="w-40 h-40 md:w-[200px] md:h-[200px] rounded-3xl object-cover border border-border"
            />
            <div className="min-w-0">
              <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-tight">{author.name}</h1>
              <p className="mt-3 text-lg text-muted-foreground">{author.role}</p>
              <div className="mt-6 space-y-4 max-w-3xl">
                {author.bio.map((p, i) => (
                  <p key={i} className="text-[1.05rem] leading-relaxed text-foreground/85">{p}</p>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <ButtonLink to={"/contact/" as any} params={{} as any} search={{ location: "", service: "" } as any}>Work with {author.name.split(" ").slice(-1)[0]}</ButtonLink>
                <ButtonLink to={"/blog/" as any} params={{} as any} search={{ tag: "", page: 1 } as any} variant="outline">Read the publication</ButtonLink>
              </div>
            </div>
          </div>
        </section>

        <section className="container-page pb-12 grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border border-border p-8 bg-card/30">
            <EyebrowLabel>Areas of expertise</EyebrowLabel>
            <ul className="mt-4 space-y-2 text-sm text-foreground/85">
              {author.expertise.map((e) => (
                <li key={e} className="flex gap-2"><span className="text-primary">·</span>{e}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-3xl border border-border p-8 bg-card/30">
            <EyebrowLabel>Experience</EyebrowLabel>
            <ul className="mt-4 space-y-2 text-sm text-foreground/85">
              {author.credentials.map((c) => (
                <li key={c} className="flex gap-2"><span className="text-primary">·</span>{c}</li>
              ))}
            </ul>
          </div>
        </section>

        {(author.sections?.length || author.faqs?.length) && (
          <section className="container-page pb-12">
            <div className="max-w-3xl">
              {(author.sections ?? []).map((s, i) => (
                <div key={i} className={i === 0 ? "" : "mt-14"}>
                  <h2 className="font-display text-2xl md:text-3xl tracking-tight leading-tight">{s.heading}</h2>
                  <div className="mt-5 space-y-5">
                    {s.body.map((p, j) => (
                      <p key={j} className="text-[1.05rem] leading-relaxed text-foreground/85">{p}</p>
                    ))}
                  </div>
                  {s.bullets && s.bullets.length > 0 && (
                    <ul className="mt-6 space-y-3">
                      {s.bullets.map((b, j) => (
                        <li key={j} className="flex gap-3 text-[1.02rem] leading-relaxed text-foreground/85">
                          <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              {author.faqs && author.faqs.length > 0 && (
                <div className="mt-16">
                  <h2 className="font-display text-2xl md:text-3xl tracking-tight leading-tight">
                    Frequently asked questions
                  </h2>
                  <dl className="mt-6 space-y-6">
                    {author.faqs.map((f, i) => (
                      <div key={i} className="rounded-2xl border border-border p-6">
                        <dt className="font-display text-lg tracking-tight">{f.q}</dt>
                        <dd className="mt-2 text-foreground/85 leading-relaxed">{f.a}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
            </div>
          </section>
        )}

        <section className="container-page pb-32">

          <h2 className="font-display text-2xl md:text-3xl tracking-tight">
            Articles by {author.name} ({posts.length})
          </h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3">
            {posts.map((p) => (
              <a
                key={p.slug}
                href={`/${p.slug}/`}
                className="group rounded-2xl border border-border p-6 bg-card/30 hover:bg-card/50 hover:border-primary/40 transition-all"
              >
                <EyebrowLabel>{p.category}</EyebrowLabel>
                <h3 className="mt-3 font-display text-xl leading-tight tracking-tight">{p.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{p.dek}</p>
                <div className="mt-4 text-xs uppercase tracking-widest text-muted-foreground">
                  {p.readingMinutes} min read
                </div>
              </a>
            ))}
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
